import { say } from '@/lib/vocabulary';
import { useState } from 'react';
import { Button } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';

/**
 * Sending one tool's files to a folder you name.
 *
 * The backend has always accepted placement rules and nothing here could make
 * one, so the layout was whatever the grouping choice produced. This is the
 * override: pick the software, say which kind of thing, name the folder.
 *
 * Every constraint below is the server's own, read from its validator rather
 * than guessed, and enforced here so a rule cannot be submitted that the API
 * will reject: tool and entity class are uppercase tokens, exactly one
 * selector field is required, and the target is a lowercase path that must end
 * in a slash and must not begin with one.
 */

/** Selector kinds the server recognises by name; anything else is recorded as
 *  a source-explicit attribute, which is still valid and still exact. */
const SELECTORS: { key: string; label: string }[] = [
  { key: 'channelId', label: 'a channel' },
  { key: 'repositoryId', label: 'a repository' },
  { key: 'productId', label: 'a product' },
  { key: 'featureId', label: 'a feature' },
  { key: 'personaId', label: 'a person' },
  { key: 'parentNativeId', label: 'a parent item' },
];

export interface ExistingRule {
  id: string;
  tool: string;
  entityClass: string;
  selectorKind: string;
  selectorValue: string;
  target: string;
  basis: string;
}

export function readRules(value: unknown): ExistingRule[] {
  if (!value || typeof value !== 'object') return [];
  const v = (value as Record<string, unknown>).rules;
  if (!Array.isArray(v)) return [];
  return (v as Record<string, unknown>[]).map((r) => ({
    id: String(r.id ?? ''),
    tool: String(r.tool ?? ''),
    entityClass: String(r.entityClass ?? ''),
    selectorKind: String(r.selectorKind ?? ''),
    selectorValue: String(r.selectorValue ?? ''),
    target: String(r.target ?? ''),
    basis: String(r.basis ?? ''),
  }));
}

/** The server's own target rule, so an invalid path is caught before it is sent. */
export function targetProblem(value: string): string | null {
  if (!value) return 'Name the folder this should go to.';
  if (value.startsWith('/')) return 'A folder path must not start with a slash.';
  if (!value.endsWith('/')) return 'A folder path must end with a slash.';
  if (value.includes('//')) return 'A folder path must not contain an empty step.';
  if (!/^[a-z0-9\-_./{}:]+$/.test(value)) return 'Use lowercase letters, digits, dash, underscore, dot and slash only.';
  return null;
}

/** Uppercase safe token, the form the server requires for tool and class. */
export function asToken(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
}

export function PlacementRules({
  tools,
  rules,
  busy,
  onAdd,
}: {
  tools: string[];
  rules: ExistingRule[];
  busy: boolean;
  onAdd: (rule: {
    match: { tool: string; entityClass: string; selector: Record<string, string> };
    target: string;
    basis: string;
  }) => void;
}) {
  const [tool, setTool] = useState(tools[0] ?? '');
  const [entityClass, setEntityClass] = useState('');
  const [selectorKey, setSelectorKey] = useState(SELECTORS[0]!.key);
  const [selectorValue, setSelectorValue] = useState('');
  const [target, setTarget] = useState('');
  const [basis, setBasis] = useState('');

  const problem = targetProblem(target);
  const ready =
    Boolean(tool) && Boolean(entityClass.trim()) && Boolean(selectorValue.trim()) && Boolean(basis.trim()) && !problem;

  const field = 'w-full rounded border border-line bg-panel-2 px-2.5 py-1.5 text-small text-ink placeholder:text-ink-4 focus:border-acc focus:outline-none';

  return (
    <div className="px-4 py-3">
      {rules.length > 0 && (
        <div className="mb-3 overflow-hidden rounded border border-line">
          {rules.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-line px-3 py-2 text-caption last:border-0">
              <ToolIcon tool={r.tool} size={14} />
              <span className="text-ink">{r.tool.replace(/[-_]/g, ' ')}</span>
              <span className="text-ink-3">{say(r.entityClass)}</span>
              {/* The selector is the rule's condition: which files this moves.
                  It was drawn in the same faint grey as the footnote beneath
                  it, so the one clause that decides what happens read as an
                  aside. */}
              <span className="text-ink-2">where {say(r.selectorKind)} is {r.selectorValue}</span>
              <span className="text-ink-3">goes to</span>
              <span className="mono text-acc-2">{r.target}</span>
              <span className="w-full text-ink-4">{r.basis}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-caption text-ink-3">Software</span>
          <select value={tool} onChange={(e) => setTool(e.target.value)} className={field}>
            {tools.map((t) => (
              <option key={t} value={t}>
                {t.replace(/[-_]/g, ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-caption text-ink-3">Kind of thing</span>
          <input
            value={entityClass}
            onChange={(e) => setEntityClass(e.target.value)}
            placeholder="MESSAGE, DOCUMENT, ISSUE"
            className={field}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-caption text-ink-3">Matching</span>
          <select value={selectorKey} onChange={(e) => setSelectorKey(e.target.value)} className={field}>
            {SELECTORS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-caption text-ink-3">Whose value is</span>
          <input
            value={selectorValue}
            onChange={(e) => setSelectorValue(e.target.value)}
            placeholder="the exact id"
            className={field}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-caption text-ink-3">Goes to this folder</span>
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value.toLowerCase())}
            placeholder="support/escalations/"
            className={field}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-caption text-ink-3">Why</span>
          <input
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            placeholder="recorded against the package"
            className={field}
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          disabled={!ready || busy}
          loading={busy}
          onClick={() =>
            onAdd({
              match: {
                tool: asToken(tool),
                entityClass: asToken(entityClass),
                selector: { [selectorKey]: selectorValue.trim() },
              },
              target,
              basis: basis.trim(),
            })
          }
        >
          {busy ? 'Saving…' : 'Move these files'}
        </Button>
        {/* A refusal to save and a reassurance that nothing is destroyed were
            the same faint grey in the same slot, so the reason a rule would
            not save was the quietest text beside the button that refused. */}
        <span className={`min-w-0 flex-1 text-caption ${problem && target ? 'text-warn' : 'text-ink-4'}`}>
          {problem && target
            ? problem
            : 'Nothing is deleted or renamed at source. This only changes which folder the files land in inside the package.'}
        </span>
      </div>
    </div>
  );
}
